import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Tabs,
  Tab,
  Box,
  Typography,
  Alert,
  Button,
} from '@mui/material';
import { Facebook, Settings } from '@mui/icons-material';
import { toast } from 'sonner';
import { facebookStatusCache } from '../utils/facebookStatusCache';
import GPTPromptSettings from './GPTPromptSettings';
import type { FacebookLoginResponse } from '../types/facebook';
import { useMediaQuery } from '@mui/material';
import Config from '../config/constants';

// Custom Threads Icon Component - 실제 Threads 공식 아이콘
const ThreadsIcon = ({ sx, ...props }: { sx?: any; [key: string]: any }) => (
  <svg
    aria-label='Threads'
    fill='currentColor'
    height='24'
    role='img'
    viewBox='0 0 192 192'
    width='24'
    style={{ ...sx }}
    {...props}
  >
    <path d='M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.7443C82.2364 44.7443 69.7731 51.1409 62.102 62.7807L75.881 72.2328C81.6116 63.5383 90.6052 61.6848 97.2286 61.6848C97.3051 61.6848 97.3819 61.6848 97.4576 61.6855C105.707 61.7381 111.932 64.1366 115.961 68.814C118.893 72.2193 120.854 76.925 121.825 82.8638C114.511 81.6207 106.601 81.2385 98.145 81.7233C74.3247 83.0954 59.0111 96.9879 60.0396 116.292C60.5615 126.084 65.4397 134.508 73.775 140.011C80.8224 144.663 89.899 146.938 99.3323 146.423C111.79 145.74 121.563 140.987 128.381 132.296C133.559 125.696 136.834 117.143 138.28 106.366C144.217 109.949 148.617 114.664 151.047 120.332C155.179 129.967 155.42 145.8 142.501 158.708C131.182 170.016 117.576 174.908 97.0135 175.059C74.2042 174.89 56.9538 167.575 45.7381 153.317C35.2355 139.966 29.8077 120.682 29.6052 96C29.8077 71.3178 35.2355 52.0336 45.7381 38.6827C56.9538 24.4249 74.2039 17.11 97.0132 16.9405C119.988 17.1113 137.539 24.4614 149.184 38.788C154.894 45.8136 159.199 54.6488 162.037 64.9503L178.184 60.6422C174.744 47.9622 169.331 37.0357 161.965 27.974C147.036 9.60668 125.202 0.195148 97.0695 0H96.9569C68.8816 0.19447 47.2921 9.6418 32.7883 28.0793C19.8819 44.4864 13.2244 67.3157 13.0007 95.9325L13 96L13.0007 96.0675C13.2244 124.684 19.8819 147.514 32.7883 163.921C47.2921 182.358 68.8816 191.806 96.9569 192H97.0695C122.03 191.827 139.624 185.292 154.118 170.811C173.081 151.866 172.51 128.119 166.26 113.541C161.776 103.087 153.227 94.5962 141.537 88.9883ZM98.4405 129.507C88.0005 130.095 77.1544 125.409 76.6196 115.372C76.2232 107.93 81.9158 99.626 99.0812 98.6368C101.047 98.5234 102.976 98.468 104.871 98.468C111.106 98.468 116.939 99.0737 122.242 100.233C120.264 124.935 108.662 128.946 98.4405 129.507Z' />
  </svg>
);

// Custom Facebook Icon Component - 간단한 Facebook 아이콘
const FacebookIcon = ({ sx, ...props }: { sx?: any; [key: string]: any }) => (
  <svg
    viewBox='0 0 24 24'
    fill='currentColor'
    height='24'
    width='24'
    style={{ ...sx }}
    {...props}
  >
    <path d='M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z' />
  </svg>
);

// Custom Instagram Icon Component - 실제 Instagram 공식 아이콘
const InstagramIcon = ({ sx, ...props }: { sx?: any; [key: string]: any }) => (
  <svg
    aria-label='Instagram'
    fill='currentColor'
    height='24'
    role='img'
    viewBox='0 0 24 24'
    width='24'
    style={{ ...sx }}
    {...props}
  >
    <path d='M12 2.982c2.937 0 3.285.011 4.445.064a6.087 6.087 0 0 1 2.042.379 3.408 3.408 0 0 1 1.265.823 3.408 3.408 0 0 1 .823 1.265 6.087 6.087 0 0 1 .379 2.042c.053 1.16.064 1.508.064 4.445s-.011 3.285-.064 4.445a6.087 6.087 0 0 1-.379 2.042 3.643 3.643 0 0 1-2.088 2.088 6.087 6.087 0 0 1-2.042.379c-1.16.053-1.508.064-4.445.064s-3.285-.011-4.445-.064a6.087 6.087 0 0 1-2.043-.379 3.408 3.408 0 0 1-1.264-.823 3.408 3.408 0 0 1-.823-1.265 6.087 6.087 0 0 1-.379-2.042c-.053-1.16-.064-1.508-.064-4.445s.011-3.285.064-4.445a6.087 6.087 0 0 1 .379-2.042 3.408 3.408 0 0 1 .823-1.265 3.408 3.408 0 0 1 1.265-.823 6.087 6.087 0 0 1 2.042-.379c1.16-.053 1.508-.064 4.445-.064M12 1c-2.987 0-3.362.013-4.535.066a8.074 8.074 0 0 0-2.67.511 5.392 5.392 0 0 0-1.949 1.27 5.392 5.392 0 0 0-1.269 1.948 8.074 8.074 0 0 0-.51 2.67C1.012 8.638 1 9.013 1 12s.013 3.362.066 4.535a8.074 8.074 0 0 0 .511 2.67 5.392 5.392 0 0 0 1.27 1.949 5.392 5.392 0 0 0 1.948 1.269 8.074 8.074 0 0 0 2.67.51C8.638 22.988 9.013 23 12 23s3.362-.013 4.535-.066a8.074 8.074 0 0 0 2.67-.511 5.625 5.625 0 0 0 3.218-3.218 8.074 8.074 0 0 0 .51-2.67C22.988 15.362 23 14.987 23 12s-.013-3.362-.066-4.535a8.074 8.074 0 0 0-.511-2.67 5.392 5.392 0 0 0-1.27-1.949 5.392 5.392 0 0 0-1.948-1.269 8.074 8.074 0 0 0-2.67-.51C15.362 1.012 14.987 1 12 1Zm0 5.351A5.649 5.649 0 1 0 17.649 12 5.649 5.649 0 0 0 12 6.351Zm0 9.316A3.667 3.667 0 1 1 15.667 12 3.667 3.667 0 0 1 12 15.667Zm5.872-10.859a1.32 1.32 0 1 0 1.32 1.32 1.32 1.32 0 0 0-1.32-1.32Z'></path>
  </svg>
);

interface SocialAccountConnectorProps {
  open: boolean;
  onClose: () => void;
  branchId: number;
  onConnect?: (platform: string, credentials: any) => void;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <Box
      role='tabpanel'
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </Box>
  );
}

const SocialAccountConnector: React.FC<SocialAccountConnectorProps> = ({
  open,
  onClose,
  branchId,
  onConnect,
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [facebookConnected, setFacebookConnected] = useState(false);
  const [facebookLoading, setFacebookLoading] = useState(false);
  const [facebookUsername, setFacebookUsername] = useState('');

  // Threads 전용 연결 상태
  const [threadsConnected, setThreadsConnected] = useState(false);
  const [threadsLoading, setThreadsLoading] = useState(false);
  const [threadsUsername, setThreadsUsername] = useState('');
  const [threadsUserId, setThreadsUserId] = useState('');

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const isMobile = useMediaQuery('(max-width:600px)');

  // 실제 Facebook 연결 상태 확인 (전역 캐시 사용)
  const checkFacebookStatus = async (forceRefresh: boolean = false) => {
    if (!branchId) return;

    try {
      // 🎯 전역 캐시를 사용하여 Facebook 연결 상태 확인
      const facebookStatus = await facebookStatusCache.getStatus(
        branchId,
        forceRefresh,
      );

      if (facebookStatus) {
        setFacebookConnected(facebookStatus.isConnected);
        setFacebookUsername(facebookStatus.userInfo?.name || 'Facebook 사용자');
        console.log('📘 Facebook 연결 상태 확인 완료 (전역 캐시):', {
          isConnected: facebookStatus.isConnected,
          username: facebookStatus.userInfo?.name,
        });
      } else {
        console.warn('❌ Facebook 상태 확인 실패');
        setFacebookConnected(false);
        setFacebookUsername('');
      }
    } catch (error) {
      console.error('❌ Facebook 상태 확인 오류:', error);
      setFacebookConnected(false);
      setFacebookUsername('');
    }
  };

  // Threads 연결 상태 확인
  const checkThreadsStatus = async () => {
    if (!branchId) return;

    try {
      const apiUrl = `${Config.API_HOST}/api/threads/status`;
      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: branchId }),
      });

      if (response.ok) {
        const data = await response.json();
        setThreadsConnected(data.isConnected || false);
        setThreadsUsername(data.username || '');
        setThreadsUserId(data.userId || '');
        console.log('🧵 Threads 연결 상태 확인 완료:', {
          isConnected: data.isConnected,
          username: data.username,
          userId: data.userId,
        });
      } else {
        console.warn('❌ Threads 상태 확인 실패');
        setThreadsConnected(false);
        setThreadsUsername('');
        setThreadsUserId('');
      }
    } catch (error) {
      console.error('❌ Threads 상태 확인 오류:', error);
      setThreadsConnected(false);
      setThreadsUsername('');
      setThreadsUserId('');
    }
  };

  // 다이얼로그가 열릴 때마다 연결 상태 확인
  useEffect(() => {
    if (open && branchId) {
      checkFacebookStatus();
      checkThreadsStatus();
    }
  }, [open, branchId]);



  /**
   * 기존 서버 기반 Facebook OAuth 연결 (기존 방식 유지)
   */
  const handleFacebookOAuthConnect = () => {
    setFacebookLoading(true);
    try {
      // Facebook OAuth 연결 로직
      const authWindow = window.open(
        `https://api.zerosalon.co.kr/api/facebook-oauth/auth?userId=${branchId}`,
        'facebook-auth',
        'width=600,height=600,scrollbars=yes,resizable=yes',
      );

      const messageListener = async (event: MessageEvent) => {
        /**
         * 🔴 `http://` 로 검증하고 있었다 — 위(:205)에서 팝업을 **https 로 열어 놓고**
         *    origin 은 http 를 기대해서 **성공 메시지가 항상 무시됐다.**
         *    연동이 되는 것처럼 보여도 화면이 갱신되지 않았다. (2026-09-01 발견)
         */
        if (event.origin !== 'https://api.zerosalon.co.kr') {
          return;
        }

        if (event.data.type === 'FACEBOOK_AUTH_SUCCESS') {
          // 연결 성공 시 캐시 무효화 후 강제 새로고침
          facebookStatusCache.invalidateCache(branchId);
          await checkFacebookStatus(true);
          toast.success(
            '🎉 Facebook 계정이 연결되었습니다! Instagram과 Threads도 함께 사용 가능합니다.',
          );
          if (onConnect) {
            onConnect('facebook', event.data.data);
          }
          window.removeEventListener('message', messageListener);
          authWindow?.close();
        } else if (event.data.type === 'FACEBOOK_AUTH_ERROR') {
          toast.error('Facebook 연결에 실패했습니다.');
          window.removeEventListener('message', messageListener);
          authWindow?.close();
        }
      };

      window.addEventListener('message', messageListener);

      // 윈도우가 닫혔는지 확인
      const checkClosed = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          setFacebookLoading(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Facebook OAuth 오류:', error);
      toast.error('Facebook 연결 중 오류가 발생했습니다.');
    } finally {
      setFacebookLoading(false);
    }
  };

  // 기본 Facebook 연결 함수 (SDK 방식 사용)
  const handleFacebookConnect = handleFacebookOAuthConnect;

  const handleFacebookDisconnect = async () => {
    if (!branchId) return;

    setFacebookLoading(true);
    try {
      const apiUrl = `${Config.API_HOST}/api/facebook-oauth/disconnect`;
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ userId: branchId }),
      });

      if (response.ok) {
        // 연결 해제 시 캐시 무효화 및 상태 업데이트
        facebookStatusCache.setStatus(branchId, false);
        setFacebookConnected(false);
        setFacebookUsername('');
        toast.success(
          '🔓 Facebook 계정 연결이 해제되었습니다. (Instagram, Threads 포함)',
        );
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || '연결 해제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('Facebook 연결 해제 오류:', error);
      toast.error(`연결 해제 실패: ${error.message}`);
    } finally {
      setFacebookLoading(false);
    }
  };

  // Threads OAuth 연결 함수
  const handleThreadsConnect = () => {
    if (!branchId) return;

    setThreadsLoading(true);
    try {
      // 실제 Threads OAuth 인증 팝업 창 열기
      const httpsApiUrl = 'https://api.zerosalon.co.kr';
      const authUrl = `${httpsApiUrl}/api/threads/auth?userId=${branchId}`;

      console.log('🧵 Threads OAuth 시작:', authUrl);

      const authWindow = window.open(
        authUrl,
        'threads-oauth',
        'width=500,height=700,scrollbars=yes,resizable=yes,toolbar=no,menubar=no,location=no,status=no',
      );

      const messageListener = async (event: MessageEvent) => {
        // 🔴 위 Facebook 과 같은 문제였다 — httpsApiUrl(:299) 로 열고 http 로 검증했다.
        if (event.origin !== 'https://api.zerosalon.co.kr') {
          return;
        }

        if (event.data.type === 'THREADS_AUTH_SUCCESS') {
          // 연결 성공 시 상태 업데이트
          await checkThreadsStatus();
          toast.success('🧵 Threads 계정이 연결되었습니다!');
          if (onConnect) {
            onConnect('threads', event.data.data);
          }
          window.removeEventListener('message', messageListener);
          authWindow?.close();
        } else if (event.data.type === 'THREADS_AUTH_ERROR') {
          toast.error(`Threads 연결에 실패했습니다: ${event.data.error}`);
          window.removeEventListener('message', messageListener);
          authWindow?.close();
        }
      };

      window.addEventListener('message', messageListener);

      // 윈도우가 닫혔는지 확인
      const checkClosed = setInterval(() => {
        if (authWindow?.closed) {
          clearInterval(checkClosed);
          window.removeEventListener('message', messageListener);
          setThreadsLoading(false);
        }
      }, 1000);
    } catch (error) {
      console.error('Threads OAuth 오류:', error);
      toast.error('Threads 연결 중 오류가 발생했습니다.');
    } finally {
      setThreadsLoading(false);
    }
  };

  // Threads 연결 해제 함수
  const handleThreadsDisconnect = async () => {
    if (!branchId) return;

    setThreadsLoading(true);
    try {
      const apiUrl = `${Config.API_HOST}/api/threads/disconnect/${branchId}`;
      const response = await fetch(apiUrl, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
          'access_token': localStorage.getItem('access_token') || '',
        },
      });

      if (response.ok) {
        setThreadsConnected(false);
        setThreadsUsername('');
        setThreadsUserId('');
        toast.success('🔓 Threads 계정 연결이 해제되었습니다.');
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || '연결 해제에 실패했습니다.');
      }
    } catch (error: any) {
      console.error('Threads 연결 해제 오류:', error);
      toast.error(`연결 해제 실패: ${error.message}`);
    } finally {
      setThreadsLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='md'
      fullWidth
      PaperProps={{
        style: { minHeight: isMobile ? '500px' : '600px' },
      }}
    >
      <DialogTitle sx={{ fontSize: isMobile ? '1.2rem' : '1.5rem' }}>
        계정 설정 및 프롬포트 설정
      </DialogTitle>
      <DialogContent>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab
              label='계정 설정'
              icon={<FacebookIcon />}
              iconPosition='start'
              sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}
            />
            <Tab 
              label='AI 설정' 
              icon={<Settings />} 
              iconPosition='start'
              sx={{ fontSize: isMobile ? '0.8rem' : '1rem' }}
            />
          </Tabs>
        </Box>

        <TabPanel value={activeTab} index={0}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: isMobile ? 2 : 4 }}>
            <Box>
              <Alert
                severity='info'
                className='mb-4'
                sx={{
                  bgcolor: 'rgba(25, 118, 210, 0.1)',
                  border: '1px solid rgba(25, 118, 210, 0.3)',
                  '& .MuiAlert-icon': { color: '#1976d2' },
                }}
              >
                <Typography variant='body2' sx={{ fontWeight: 600, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  📋 <strong>플랫폼별 연결 방식</strong>
                </Typography>
                <Typography variant='body2' sx={{ mt: 1, fontSize: isMobile ? '0.75rem' : '0.875rem' }}>
                  • <strong>Meta 통합:</strong> Facebook 연결로 Instagram도 함께
                  사용 가능
                  <br />• <strong>Threads:</strong> 별도 전용 연결로 더 안정적인
                  포스팅 지원
                </Typography>
              </Alert>
            </Box>

            {/* Meta 통합 연결 카드 */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: 4,
                p: isMobile ? 2 : 4,
                mb: isMobile ? 2 : 4,
                boxShadow: '0 8px 32px rgba(24, 119, 242, 0.1)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 2 : 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 2 : 3, width: isMobile ? '100%' : 'auto' }}>
                  <Box
                    sx={{
                      width: isMobile ? 48 : 64,
                      height: isMobile ? 48 : 64,
                      background: facebookConnected
                        ? 'linear-gradient(135deg, #4caf50 0%, #2e7d32 100%)'
                        : 'linear-gradient(135deg, #1877F2 0%, #0d47a1 100%)',
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: `0 4px 16px ${facebookConnected ? 'rgba(76, 175, 80, 0.3)' : 'rgba(24, 119, 242, 0.3)'}`,
                    }}
                  >
                    <FacebookIcon
                      sx={{ color: 'white', fontSize: isMobile ? '1.5rem' : '2rem' }}
                      style={{ color: 'white' }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      sx={{ fontWeight: 700, color: '#1877F2', mb: 0.5 }}
                    >
                      Meta 통합 연결
                    </Typography>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', fontWeight: 600, mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                    >
                      Facebook & Instagram
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: facebookConnected ? '#4caf50' : '#ff9800',
                          boxShadow: `0 0 8px ${facebookConnected ? 'rgba(76, 175, 80, 0.6)' : 'rgba(255, 152, 0, 0.6)'}`,
                        }}
                      />
                      <Typography
                        variant='body1'
                        sx={{
                          color: facebookConnected ? '#4caf50' : '#ff9800',
                          fontWeight: 600,
                          fontSize: isMobile ? '0.9rem' : '1rem',
                        }}
                      >
                        {facebookConnected ? '✅ 연결됨' : '⚠️ 연결되지 않음'}
                      </Typography>
                    </Box>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', mt: 0.5, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      {facebookConnected && facebookUsername ? (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <span>👤 {facebookUsername}</span>
                          <span>•</span>
                          <FacebookIcon sx={{ fontSize: '1rem' }} />
                          <span>Facebook</span>
                          <span>•</span>
                          <InstagramIcon sx={{ fontSize: '1rem' }} />
                          <span>Instagram</span>
                        </Box>
                      ) : (
                        <Box
                          sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}
                        >
                          <FacebookIcon sx={{ fontSize: '1rem' }} />
                          <span>Facebook</span>
                          <span>•</span>
                          <InstagramIcon sx={{ fontSize: '1rem' }} />
                          <span>Instagram</span>
                        </Box>
                      )}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                  {facebookConnected ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: isMobile ? 'stretch' : 'flex-end',
                      }}
                    >
                      <Button
                        onClick={handleFacebookDisconnect}
                        disabled={facebookLoading}
                        variant='outlined'
                        color='error'
                        sx={{
                          borderRadius: 3,
                          px: isMobile ? 2 : 3,
                          py: isMobile ? 0.8 : 1,
                          fontWeight: 700,
                          borderWidth: 2,
                          fontSize: isMobile ? '0.8rem' : '1rem',
                          width: isMobile ? '100%' : 'auto',
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                          },
                        }}
                        style={{ color: 'black', borderColor: 'black' }}
                      >
                        {facebookLoading ? '🔄 해제 중...' : '연결 해제'}
                      </Button>
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.75rem', textAlign: isMobile ? 'center' : 'right' }}
                      >
                        모든 플랫폼 연결 해제됨
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: isMobile ? 'stretch' : 'flex-end',
                      }}
                    >
                      <Button
                        onClick={handleFacebookConnect}
                        disabled={facebookLoading}
                        variant='contained'
                        sx={{
                          borderRadius: 3,
                          px: isMobile ? 3 : 4,
                          py: isMobile ? 1.2 : 1.5,
                          fontWeight: 700,
                          fontSize: isMobile ? '0.9rem' : '1rem',
                          width: isMobile ? '100%' : 'auto',
                          background:
                            'linear-gradient(135deg, #1877F2 0%, #0d47a1 100%)',
                          boxShadow: '0 4px 16px rgba(24, 119, 242, 0.3)',
                          '&:hover': {
                            background:
                              'linear-gradient(135deg, #0d47a1 0%, #1565c0 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(24, 119, 242, 0.4)',
                          },
                          '&:disabled': {
                            background:
                              'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                            color: '#757575',
                          },
                        }}
                      >
                        {facebookLoading ? '🔄 연결 중...' : '🚀 연결하기'}
                      </Button>
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.75rem', textAlign: isMobile ? 'center' : 'right' }}
                      >
                        3개 플랫폼 동시 연결
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {facebookConnected && (
                <Box
                  sx={{
                    mt: 3,
                    pt: 3,
                    borderTop: '2px solid rgba(76, 175, 80, 0.2)',
                    bgcolor: 'rgba(76, 175, 80, 0.05)',
                    borderRadius: 2,
                    p: isMobile ? 1.5 : 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        bgcolor: '#4caf50',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ color: 'white', fontSize: isMobile ? '1rem' : '1.2rem' }}>
                        ✅
                      </Typography>
                    </Box>
                    <Typography
                      variant={isMobile ? 'body1' : 'h6'}
                      sx={{ color: '#4caf50', fontWeight: 700 }}
                    >
                      연결 완료!
                    </Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    sx={{ color: '#2e7d32', fontWeight: 600, mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                  >
                    🎉 Facebook 계정이 성공적으로 연결되었습니다.
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: '#388e3c', lineHeight: 1.6, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  >
                    이제 다음 플랫폼들에 자동으로 포스팅할 수 있습니다:
                  </Typography>
                  <Box
                    sx={{ mt: 1.5, display: 'flex', gap: isMobile ? 1 : 2, flexWrap: 'wrap' }}
                  >
                    <Box
                      sx={{
                        px: isMobile ? 1.5 : 2,
                        py: 0.5,
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        borderRadius: 2,
                        fontSize: isMobile ? '0.75rem' : '0.85rem',
                        fontWeight: 600,
                        color: '#2e7d32',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <FacebookIcon sx={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }} />
                      Facebook
                    </Box>
                    <Box
                      sx={{
                        px: isMobile ? 1.5 : 2,
                        py: 0.5,
                        bgcolor: 'rgba(76, 175, 80, 0.1)',
                        border: '1px solid rgba(76, 175, 80, 0.3)',
                        borderRadius: 2,
                        fontSize: isMobile ? '0.75rem' : '0.85rem',
                        fontWeight: 600,
                        color: '#2e7d32',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <InstagramIcon sx={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }} />
                      Instagram
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>

            {/* Threads 전용 연결 카드 */}
            <Box
              sx={{
                background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                borderRadius: 4,
                p: isMobile ? 2 : 4,
                boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: isMobile ? 'flex-start' : 'center',
                  justifyContent: 'space-between',
                  flexDirection: isMobile ? 'column' : 'row',
                  gap: isMobile ? 2 : 0,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: isMobile ? 2 : 3, width: isMobile ? '100%' : 'auto' }}>
                  <Box
                    sx={{
                      width: isMobile ? 48 : 64,
                      height: isMobile ? 48 : 64,
                      background:
                        'linear-gradient(135deg, #000000 0%, #333333 100%)',
                      borderRadius: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                    }}
                  >
                    <ThreadsIcon sx={{ color: 'white', fontSize: isMobile ? '1.5rem' : '2rem' }} />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <Typography
                      variant={isMobile ? 'h6' : 'h5'}
                      sx={{ fontWeight: 700, color: '#000000', mb: 0.5 }}
                    >
                      Threads 전용 연결
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: threadsConnected ? '#4caf50' : '#ff9800',
                          boxShadow: `0 0 8px ${threadsConnected ? 'rgba(76, 175, 80, 0.6)' : 'rgba(255, 152, 0, 0.6)'}`,
                        }}
                      />
                      <Typography
                        variant='body1'
                        sx={{
                          color: threadsConnected ? '#4caf50' : '#ff9800',
                          fontWeight: 600,
                          fontSize: isMobile ? '0.9rem' : '1rem',
                        }}
                      >
                        {threadsConnected ? '✅ 연결됨' : '⚠️ 연결되지 않음'}
                      </Typography>
                    </Box>
                    <Typography
                      variant='body2'
                      sx={{ color: 'text.secondary', mt: 0.5, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                    >
                      {threadsConnected && threadsUsername
                        ? `👤 ${threadsUsername} • ID: ${threadsUserId}`
                        : 'Threads 전용 API 연결 • App ID: 725066299902413'}
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ width: isMobile ? '100%' : 'auto' }}>
                  {threadsConnected ? (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: isMobile ? 'stretch' : 'flex-end',
                      }}
                    >
                      <Button
                        onClick={handleThreadsDisconnect}
                        disabled={threadsLoading}
                        variant='outlined'
                        color='error'
                        sx={{
                          borderRadius: 3,
                          px: isMobile ? 2 : 3,
                          py: isMobile ? 0.8 : 1,
                          fontWeight: 700,
                          borderWidth: 2,
                          fontSize: isMobile ? '0.8rem' : '1rem',
                          width: isMobile ? '100%' : 'auto',
                          '&:hover': {
                            borderWidth: 2,
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(244, 67, 54, 0.3)',
                          },
                        }}
                        style={{
                          color: 'black',
                          borderColor: 'black',
                        }}
                      >
                        {threadsLoading ? '🔄 해제 중...' : '연결 해제'}
                      </Button>
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.75rem', textAlign: isMobile ? 'center' : 'right' }}
                      >
                        Threads 전용 연결 해제
                      </Typography>
                    </Box>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 1,
                        alignItems: isMobile ? 'stretch' : 'flex-end',
                      }}
                    >
                      <Button
                        onClick={handleThreadsConnect}
                        disabled={threadsLoading}
                        variant='contained'
                        sx={{
                          borderRadius: 3,
                          px: isMobile ? 3 : 4,
                          py: isMobile ? 1.2 : 1.5,
                          fontWeight: 700,
                          fontSize: isMobile ? '0.9rem' : '1rem',
                          width: isMobile ? '100%' : '220px',
                          background:
                            'linear-gradient(135deg, #000000 0%, #333333 100%)',
                          boxShadow: '0 4px 16px rgba(0, 0, 0, 0.3)',
                          '&:hover': {
                            background:
                              'linear-gradient(135deg, #333333 0%, #555555 100%)',
                            transform: 'translateY(-2px)',
                            boxShadow: '0 6px 20px rgba(0, 0, 0, 0.4)',
                          },
                          '&:disabled': {
                            background:
                              'linear-gradient(135deg, #e0e0e0 0%, #bdbdbd 100%)',
                            color: '#757575',
                          },
                        }}
                        style={{ color: 'white' }}
                      >
                        {threadsLoading ? (
                          '🔄 인증 중...'
                        ) : (
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 0.5,
                              justifyContent: 'center',
                            }}
                            style={{ fontSize: isMobile ? '0.9rem' : '1rem' }}
                          >
                            <ThreadsIcon sx={{ fontSize: isMobile ? '0.9rem' : '1rem' }} />
                            Threads 로그인
                          </Box>
                        )}
                      </Button>
                      <Typography
                        variant='caption'
                        sx={{ color: 'text.secondary', fontSize: isMobile ? '0.7rem' : '0.75rem', textAlign: isMobile ? 'center' : 'right' }}
                      >
                        Threads 계정으로 로그인
                      </Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {threadsConnected && (
                <Box
                  sx={{
                    mt: 3,
                    pt: 3,
                    borderTop: '2px solid rgba(76, 175, 80, 0.2)',
                    bgcolor: 'rgba(76, 175, 80, 0.05)',
                    borderRadius: 2,
                    p: isMobile ? 1.5 : 2,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 2,
                      mb: 2,
                    }}
                  >
                    <Box
                      sx={{
                        width: isMobile ? 28 : 32,
                        height: isMobile ? 28 : 32,
                        bgcolor: '#4caf50',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Typography sx={{ color: 'white', fontSize: isMobile ? '1rem' : '1.2rem' }}>
                        ✅
                      </Typography>
                    </Box>
                    <Typography
                      variant={isMobile ? 'body1' : 'h6'}
                      sx={{ color: '#4caf50', fontWeight: 700 }}
                    >
                      Threads 연결 완료!
                    </Typography>
                  </Box>
                  <Typography
                    variant='body2'
                    sx={{ color: '#2e7d32', fontWeight: 600, mb: 1, fontSize: isMobile ? '0.8rem' : '0.875rem' }}
                  >
                    🎉 Threads 계정이 성공적으로 연결되었습니다.
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ color: '#388e3c', lineHeight: 1.6, fontSize: isMobile ? '0.75rem' : '0.875rem' }}
                  >
                    이제 Threads에 직접 포스팅할 수 있습니다.
                  </Typography>
                  <Box
                    sx={{ mt: 1.5, display: 'flex', gap: isMobile ? 1 : 2, flexWrap: 'wrap' }}
                  >
                    <Box
                      sx={{
                        px: isMobile ? 1.5 : 2,
                        py: 0.5,
                        bgcolor: 'rgba(0, 0, 0, 0.1)',
                        border: '1px solid rgba(0, 0, 0, 0.3)',
                        borderRadius: 2,
                        fontSize: isMobile ? '0.75rem' : '0.85rem',
                        fontWeight: 600,
                        color: '#000000',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                      }}
                    >
                      <ThreadsIcon sx={{ fontSize: isMobile ? '0.75rem' : '0.85rem' }} />
                      Threads 전용 API
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Box>
        </TabPanel>

        <TabPanel value={activeTab} index={1}>
          <GPTPromptSettings branchId={branchId} />
        </TabPanel>
      </DialogContent>
    </Dialog>
  );
};

export default SocialAccountConnector;
